import { FC, useRef } from "react";
import { CoachDetail } from "../../../../../../../../api/services/common/models";
import { IntakeDetail } from "../../../../../../../../api/services/program/models";
import {
  ChipOptionsDataType,
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiTypography,
} from "../../../../../../../../components";
import MuiChipWithPopover from "../../../../../../../../components/chipWithPopover/MuiChipWithPopover";
import { EMPTY_VALUE_DASH } from "../../../../../../../../helpers/constants";
import {
  getFullName,
  getValueOrBlank,
  isEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { ParticipantDetail } from "../../../../../../../createProgram/modelsAndSchemas/Models";
import { programSiteCoreKeys } from "../../../../../../../createProgram/siteCore/SiteCoreKeys";
import { ParticipantIntakeSelectionWrapper } from "./ParticipantIntakeSelection.style";

interface ParticipantIntakeSelectionProp {
   intakeDetails?: IntakeDetail;
}

export const ParticipantIntakeSelection: FC<ParticipantIntakeSelectionProp> = ({
  intakeDetails,
}) => {
  const { fetchLabel, fetchValueByKeyOfDropdown } = useSiteCore();
  const { gridSizeMedium: gridSize, gridSizeMediumClass } = useGridContainer();
  const { commonARIALabel } = useNavigationSiteCore();
  const getLabel = (key: string) => {
    return fetchLabel(programSiteCoreKeys.intakeSelection, key);
  };

  const coachBiosRequestedGridRef = useRef<HTMLDivElement>(null);
  const coachBiosDeclinedGridRef = useRef<HTMLDivElement>(null);
  const coachBiosSentGridRef = useRef<HTMLDivElement>(null);



  const getCoachChips = (coachIds: CoachDetail[] = []) => {
    return coachIds.map((item: CoachDetail) => ({
      label: getFullName(item.firstName, item.lastName),
      value: item.id,
    })) as ChipOptionsDataType[];
  };

  return (
    <ParticipantIntakeSelectionWrapper>
      <MuiBox className="content">
        <MuiTypography
          component="h2"
          className="sub-label-style"
          variant="h2SemiBold"
        >
          {getLabel(programSiteCoreKeys.intakeSection)}
        </MuiTypography>
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.intakeStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.intakeSelection,
                programSiteCoreKeys.intakeStatusList,
                getValueOrBlank(intakeDetails?.intakeStatusId),
              )}
            />
          </MuiGrid>

          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            ref={coachBiosRequestedGridRef}
          >
            <MuiTypography
              component={"h3"}
              className="label-style"
              variant={"h3"}
            >
              {getLabel(programSiteCoreKeys.coachBiosRequested)}
            </MuiTypography>
            {!isEmpty(intakeDetails?.biosRequestedCoach) ? (
              <MuiChipWithPopover
                data={getCoachChips(intakeDetails?.biosRequestedCoach)}
                handleDelete={() => {}}
                isEditable={false}
                headerLabel={getLabel(programSiteCoreKeys.coachBiosRequested)}
                gridRef={coachBiosRequestedGridRef}
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              <MuiTypography
                component={"h4"}
                className="value-style"
                variant={"h4Bold"}
              >
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            )}
          </MuiGrid>

          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            ref={coachBiosDeclinedGridRef}
          >
            <MuiTypography
              component={"h3"}
              className="label-style"
              variant={"h3"}
            >
              {getLabel(programSiteCoreKeys.biosDeclined)}
            </MuiTypography>
            {!isEmpty(intakeDetails?.biosDeclinedCoach) ? (
              <MuiChipWithPopover
                data={getCoachChips(intakeDetails?.biosDeclinedCoach)}
                handleDelete={() => {}}
                isEditable={false}
                headerLabel={getLabel(programSiteCoreKeys.biosDeclined)}
                gridRef={coachBiosDeclinedGridRef}
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              <MuiTypography
                component={"h4"}
                className="value-style"
                variant={"h4Bold"}
              >
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            )}
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="content">
        <MuiTypography
          component="h2"
          className="sub-label-style"
          variant="h2SemiBold"
        >
          {getLabel(programSiteCoreKeys.bioStatusSection)}
        </MuiTypography>
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.bioStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.intakeSelection,
                programSiteCoreKeys.bioStatusList,
                getValueOrBlank(intakeDetails?.bioStatusId),
              )}
            />
          </MuiGrid>

          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            ref={coachBiosSentGridRef}
          >
            <MuiTypography
              component={"h3"}
              className="label-style"
              variant={"h3"}
            >
              {getLabel(programSiteCoreKeys.biosSent)}
            </MuiTypography>
            {!isEmpty(intakeDetails?.biosSentCoach) ? (
              <MuiChipWithPopover
                data={getCoachChips(intakeDetails?.biosSentCoach)}
                handleDelete={() => {}}
                isEditable={false}
                headerLabel={getLabel(programSiteCoreKeys.biosSent)}
                gridRef={coachBiosSentGridRef}
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              <MuiTypography
                component={"h4"}
                className="value-style"
                variant={"h4Bold"}
              >
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            )}
          </MuiGrid>
        </MuiGrid>
      </MuiBox>

      <MuiBox className="content">
        <MuiTypography
          component="h2"
          className="sub-label-style"
          variant="h2SemiBold"
        >
          {getLabel(programSiteCoreKeys.chemistryMeetingSection)}
        </MuiTypography>
        <MuiGrid container data-testid="participate-details">
          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.chemistryMeetingStatus)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={fetchValueByKeyOfDropdown(
                programSiteCoreKeys.intakeSelection,
                programSiteCoreKeys.chemistryMeetingStatusList,
                getValueOrBlank(intakeDetails?.chemistryMeetingStatusId),
              )}
            />
          </MuiGrid>

          <MuiGrid
            size={gridSize}
            className={gridSizeMediumClass}
            ref={coachBiosSentGridRef}
          >
            <MuiTypography
              component={"h3"}
              className="label-style"
              variant={"h3"}
            >
              {getLabel(programSiteCoreKeys.chemistryMeetingSelected)}
            </MuiTypography>
            {!isEmpty(intakeDetails?.chemistryMeetingSelectedCoach) ? (
              <MuiChipWithPopover
                data={getCoachChips(
                  intakeDetails?.chemistryMeetingSelectedCoach,
                )}
                handleDelete={() => {}}
                isEditable={false}
                headerLabel={getLabel(
                  programSiteCoreKeys.chemistryMeetingSelected,
                )}
                gridRef={coachBiosSentGridRef}
                showMoreARIALabel={commonARIALabel.showMoreDetails}
                removeChipNameARIALabel={commonARIALabel.removeChipName}
                closePopoverARIALabel={commonARIALabel.closePopover}
              />
            ) : (
              <MuiTypography
                component={"h4"}
                className="value-style"
                variant={"h4Bold"}
              >
                {EMPTY_VALUE_DASH}
              </MuiTypography>
            )}
          </MuiGrid>

          <MuiGrid size={gridSize} className={gridSizeMediumClass}>
            <MuiDoubleLabel
              label={getLabel(programSiteCoreKeys.coachSelected)}
              replaceEmptyWithDash
              isValueWithEllipsis
              value={intakeDetails?.coachSelectedCoach?.map(
                (item: CoachDetail) =>
                  getFullName(item.firstName, item.lastName),
              )}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </ParticipantIntakeSelectionWrapper>
  );
};
